import { api, readTotalCount } from '../api/client';
import {
	API_ADMIN_ATTACHMENTS_URL,
	API_ADMIN_ATTACHMENT_TRANSACTIONS_URL,
	API_ITEM_ATTACHMENTS_URL,
	API_ITEM_ATTACHMENT_URL,
	API_ITEM_ATTACHMENT_DOWNLOAD_URL,
	API_ITEM_ATTACHMENT_VARIANT_URL,
	API_HELD_FILES_URL,
	API_HELD_FILE_URL,
	API_HELD_FILE_TOUCH_URL,
	API_HELD_FILE_VARIANT_URL,
	API_COLLECTION_ATTACHMENTS_SUMMARY_URL,
} from '../utils';
import { saveFile } from '../platform';

/**
 * Item attachments (keepr-api). Files hung off a saved record, riding the same
 * S3-backed files pipeline as avatars (kind='attachment') but stored in a
 * private bucket and served only through the API.
 *
 * A serialized attachment:
 *   { _id, subjectType, subjectId, collection_id, originalName, ext, mime,
 *     size, status, createdBy, createdAt, updatedAt,
 *     image,      { width, height } | null — non-null only for a photo
 *                 (an iPhone HEIC included); null for a PDF
 *     variants }  the sizes the server made, from ['sm', 'md', 'lg']; a PDF's
 *                 first page has ['sm', 'md']; none → []
 *
 * The ADMIN listing enriches each row so the census can be read by a human
 * without a lookup pass per cell:
 *   collection      { _id, name, color, icon } | null   (branding, not just a
 *                                                        name — replaces the
 *                                                        old `collectionName`)
 *   createdByUser   PublicUser | null
 *   downloadCount   number
 * where PublicUser is { _id, firstName, lastName, fullName, avatar } and
 * `avatar` is {type:'photo',fileId} | {type:'icon',icon,color} | absent.
 * Both embeds are nullable: a deleted or unreadable record still lists, and
 * the UI falls back to the raw id (CollectionBadge / UserChip both do this).
 *
 * Item TITLES are not embedded — the client resolves subjects through the
 * batch `GET /api/items?ids=` endpoint and the card display template, the same
 * way every other surface builds an item's name.
 *
 * Endpoints (all JWT-authenticated through the shared axios client):
 *   GET    /api/items/{itemId}/attachments                       → bare array, newest first
 *   POST   /api/items/{itemId}/attachments                       → multipart, single field `file`; 201 + attachment
 *                                                                  413 over the size limit, 415 blocked type,
 *                                                                  403 attachments disabled / no write access
 *   GET    /api/items/{itemId}/attachments/{id}/download          → binary stream, Content-Disposition: attachment
 *          ?via=preview                                           → identical stream and headers; the server logs the
 *                                                                  transaction as a preview instead of a download.
 *                                                                  Omitted (or via=direct) for a real download.
 *          ?view=full                                             → the original's bytes for the viewer's "View full
 *                                                                  resolution" (a HEIC original arrives as a JPEG).
 *                                                                  The same gate as a download (403 copies_not_allowed),
 *                                                                  logged as a view.
 *   GET    /api/items/{itemId}/attachments/{id}/variants/{size}   → image/webp, size sm | md | lg. Readers of the item,
 *                                                                  NOT ledgered (looking, not taking); 404
 *                                                                  variant_not_found when the server made no such size.
 *   DELETE /api/items/{itemId}/attachments/{id}                   → 200/204
 *   GET    /api/admin/attachments                                 → bare array + X-Total-Count (admin only)
 *          ?q&collectionId&subjectType&ext&createdBy&skip&limit&sort_field&sort_direction
 *          rows carry `collection`, `createdByUser`, `downloadCount` (above)
 *   GET    /api/admin/attachments/{id}/transactions               → bare array, newest first (admin only)
 *          rows carry `user` (PublicUser | null) alongside the raw `userId`,
 *          and `via: 'direct' | 'preview'` on download rows. `via` is ABSENT on
 *          rows written before previews existed — treat absent as 'direct'.
 *
 * Downloads are API-proxied and need the JWT, so a plain <a href> can't fetch
 * them: pull the bytes as a blob through the authenticated client and hand the
 * browser an object URL (see downloadItemAttachment).
 */

// Server default (ATTACHMENT_MAX_BYTES on the API). Mirrored here only as a
// pre-flight courtesy — the server enforces it, and attach-admins skip the
// client check entirely because their real limit is higher.
export const ATTACHMENT_MAX_MB = 100;
export const ATTACHMENT_MAX_BYTES = ATTACHMENT_MAX_MB * 1024 * 1024;

// Mirrors the API's blocklist. Client-side it saves a pointless 100MB upload
// that would be refused with a 415; the server list is the one that counts.
export const BLOCKED_EXTENSIONS = [
	'exe', 'dll', 'msi', 'bat', 'cmd', 'com', 'scr', 'ps1', 'sh', 'jar',
	'apk', 'vbs', 'js', 'mjs', 'hta', 'cpl', 'pif', 'lnk', 'reg',
];

export function getFileExtension(name) {
	const parts = `${name || ''}`.split('.');
	return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

export function isBlockedFileName(name) {
	return BLOCKED_EXTENSIONS.includes(getFileExtension(name));
}

// `signal` (an AbortSignal) cancels a list the caller no longer needs — the
// read view's, when the record on show moves on.
export async function getItemAttachments(itemId, { signal } = {}) {
	const { data } = signal
		? await api.get(API_ITEM_ATTACHMENTS_URL(itemId), { signal })
		: await api.get(API_ITEM_ATTACHMENTS_URL(itemId));
	return data || [];
}

// `onProgress(fraction)` and `signal` are bulk attach's (KPR-187): a queue of
// many uploads shows each one's progress and can stop the rest. Callers that
// pass neither send exactly what they always did.
export async function uploadItemAttachment(itemId, file, { onProgress, signal } = {}) {
	const form = new FormData();
	form.append('file', file);
	const config = {};
	if (signal) config.signal = signal;
	if (typeof onProgress === 'function') {
		config.onUploadProgress = (event) => {
			const total = event?.total || file?.size || 0;
			if (total > 0) onProgress(Math.min(1, (event.loaded || 0) / total));
		};
	}
	const { data } = Object.keys(config).length
		? await api.post(API_ITEM_ATTACHMENTS_URL(itemId), form, config)
		: await api.post(API_ITEM_ATTACHMENTS_URL(itemId), form);
	return data;
}

export async function deleteItemAttachment(itemId, attachmentId) {
	await api.delete(API_ITEM_ATTACHMENT_URL(itemId, attachmentId));
}

/**
 * The bytes, as a Blob, through the authenticated client — the one fetch both
 * saving and previewing go through.
 *
 * `via: 'preview'` asks the server to log the transaction as a preview rather
 * than a download: same stream, same headers, different intent. Anything else
 * (including the default) is a plain download, so a caller that forgets the
 * option under-reports a preview rather than inflating the download count.
 */
export async function fetchItemAttachmentBlob(itemId, attachmentId, { via } = {}) {
	const config = { responseType: 'blob' };
	if (via === 'preview') config.params = { via: 'preview' };
	const { data } = await api.get(
		API_ITEM_ATTACHMENT_DOWNLOAD_URL(itemId, attachmentId),
		config
	);
	return data;
}

// The sizes the variants route answers. Anything else is a caller's mistake,
// refused here rather than sent as a request the server can only 404.
export const ATTACHMENT_VARIANT_SIZES = ['sm', 'md', 'lg'];

/**
 * One server-made size of a photo (or of a PDF's first page), as a Blob.
 * Looking, not taking: the server writes no ledger row for it, and every
 * reader of the item may ask — a link reader without copy rights included.
 * Callers go through `utils/attachmentVariants`, which keeps one object URL
 * per attachment and size; this is the raw fetch under it.
 */
export async function fetchItemAttachmentVariantBlob(itemId, attachmentId, size) {
	if (!ATTACHMENT_VARIANT_SIZES.includes(size)) {
		throw new Error(`Unknown attachment variant: ${size}`);
	}
	const { data } = await api.get(
		API_ITEM_ATTACHMENT_VARIANT_URL(itemId, attachmentId, size),
		{ responseType: 'blob' }
	);
	return data;
}

/**
 * The original, for the viewer's "View full resolution": the download route
 * with `view=full`. Gated exactly as a download (a reader who may not take
 * copies is refused), and logged by the server as a view. A HEIC original
 * comes back as a full-resolution JPEG so the browser can always show it.
 * Never saved: the caller renders it and revokes its object URL.
 *
 * The blob keeps the server's type only when it is a raster image. Anything
 * else (SVG, HTML, a missing type) is retyped as opaque bytes, so the object
 * URL can never render as a document, even if someone opens it in a tab. An
 * `<img>` sniffs the pixels either way.
 */
export async function fetchItemAttachmentFullBlob(itemId, attachmentId) {
	const { data } = await api.get(
		API_ITEM_ATTACHMENT_DOWNLOAD_URL(itemId, attachmentId),
		{ responseType: 'blob', params: { view: 'full' } }
	);
	const type = `${data?.type || ''}`.toLowerCase();
	if (type.startsWith('image/') && !type.includes('svg')) return data;
	return new Blob([data], { type: 'application/octet-stream' });
}

/**
 * Fetches the bytes with the authenticated client and hands them to the
 * platform to save (the browser's own save flow on the web, the share sheet in
 * the app: KPR-1668). Returns nothing; throws on failure so the caller can
 * report it. The object URL the web makes is revoked either way — it is a
 * one-shot handle, not a cacheable resource like an avatar variant.
 *
 * No `via`: this is the real thing, and it counts as a download.
 */
export async function downloadItemAttachment(itemId, attachmentId, originalName, { anchor } = {}) {
	const data = await fetchItemAttachmentBlob(itemId, attachmentId);
	await saveFile(data, originalName, { anchor });
}

/**
 * Subject dispatch. Attachments are modelled as {subjectType, subjectId} on
 * the server, so adding users/collections/cards later is one entry here plus
 * its URL constants — AttachmentsSection needs no change.
 */
export const ATTACHMENT_SUBJECTS = {
	item: {
		list: getItemAttachments,
		upload: uploadItemAttachment,
		remove: deleteItemAttachment,
		download: downloadItemAttachment,
	},
};

export function getSubjectService(subjectType = 'item') {
	return ATTACHMENT_SUBJECTS[subjectType] || null;
}

/**
 * Admin-only. `total` is the count across the WHOLE filter, not the page, so
 * the pager can size itself without a second request.
 */
export async function getAdminAttachments(params = {}) {
	const { data, headers } = await api.get(API_ADMIN_ATTACHMENTS_URL, { params });
	const rows = data || [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

export async function getAttachmentTransactions(attachmentId, params = {}) {
	const { data } = await api.get(
		API_ADMIN_ATTACHMENT_TRANSACTIONS_URL(attachmentId),
		{ params }
	);
	return data || [];
}

/**
 * HELD FILES (keepr-api SCHEMA.md § Held files; the 10b contract): the item
 * form uploads a file the moment it is picked, before the item exists, so the
 * item is saved once, with its file. A held file belongs to its uploader
 * alone, in a session; every held route answers 404 for anyone else's.
 *
 *   POST   /api/collections/{collectionId}/held-files           multipart `file`, `card_id`, `element` (+ `column`: element is then a repeating group)
 *          → 201 + a held attachment ({ _id, status: 'held', heldUntil, image, variants, … })
 *          413 file_too_large { element, limitMb } · 415 file_wrong_kind { element }
 *          409 held_files_limit { maxFiles, maxBytes } · 403 attachments off / no write
 *   POST   …/held-files/{fileId}/touch                           heldUntil → an hour from now (never earlier)
 *   DELETE …/held-files/{fileId}                                  deleted now (Cancel); 200 { ok: true }
 *   GET    …/held-files/{fileId}/variants/{size}                  image/webp, as the item variants route
 *
 * The item save sends the held id as the element's value and the server
 * claims it — the id stays the same, so a cached thumbnail stays good.
 */
export async function uploadHeldFile(collectionId, { file, cardId, element, column }, { onProgress } = {}) {
	const form = new FormData();
	form.append('file', file);
	if (cardId) form.append('card_id', cardId);
	if (element) form.append('element', element);
	// An entry's file column (KPR-750): `element` is the repeating group and
	// `column` the file column of it. No entry is named - a new entry has no id
	// yet, and a column's rules are the same in every entry.
	if (element && column) form.append('column', column);
	const config = typeof onProgress === 'function' ? {
		onUploadProgress: (event) => {
			const total = Number(event?.total) || 0;
			if (total > 0) onProgress(Math.min(100, Math.round((Number(event.loaded) || 0) * 100 / total)));
		},
	} : undefined;
	const { data } = config
		? await api.post(API_HELD_FILES_URL(collectionId), form, config)
		: await api.post(API_HELD_FILES_URL(collectionId), form);
	return data;
}

export async function touchHeldFile(collectionId, fileId) {
	await api.post(API_HELD_FILE_TOUCH_URL(collectionId, fileId));
}

export async function deleteHeldFile(collectionId, fileId) {
	await api.delete(API_HELD_FILE_URL(collectionId, fileId));
}

/** A held file's server-made size, as a Blob (through `utils/attachmentVariants`). */
export async function fetchHeldFileVariantBlob(collectionId, fileId, size) {
	if (!ATTACHMENT_VARIANT_SIZES.includes(size)) {
		throw new Error(`Unknown attachment variant: ${size}`);
	}
	const { data } = await api.get(
		API_HELD_FILE_VARIANT_URL(collectionId, fileId, size),
		{ responseType: 'blob' }
	);
	return data;
}

/**
 * What turning a collection's attachments off would hide (managers only):
 * `{ boundItems, boundFiles, otherFiles }`.
 */
export async function getCollectionAttachmentsSummary(collectionId) {
	const { data } = await api.get(API_COLLECTION_ATTACHMENTS_SUMMARY_URL(collectionId));
	return data || {};
}
